/**
 * Dirty-state tracking for the pricing grid.
 *
 * Records a per-input "baseline" value (captured once after cart
 * pre-population) and compares current values against it to determine whether
 * any quantity has changed since the page loaded / last cart update.
 *
 * Global model: any input differing from its baseline → every Update Cart
 * button on the page becomes enabled.
 */

/**
 * Baseline quantity values keyed by `${variantId}|${warehouseCode}`.
 *
 * Inputs not present in this map are treated as baseline "" (empty) — which
 * matches reality for rows that load lazily after the initial cart sync (they
 * start empty and have no cart items to restore).
 */
const baseline = new Map<string, string>();

function keyFor(input: HTMLInputElement): string | null {
  const variantId = input.dataset.variantId;
  const warehouseCode = input.dataset.warehouseCode || "WPBR";
  if (!variantId) return null;
  return `${variantId}|${warehouseCode}`;
}

function currentValue(input: HTMLInputElement): string {
  return (input.value || "").trim();
}

/**
 * Record baseline values from the current DOM.
 *
 * Only writes entries for inputs not already tracked, so calling this multiple
 * times won't clobber user edits captured earlier. Called once at init after
 * cart pre-population; post-update baselining goes through
 * setBaselinesFromSnapshot instead to avoid a DOM-read race with in-flight
 * edits.
 */
export function captureBaseline(): void {
  $(".variant_qty_input_directship").each(function () {
    const input = this as HTMLInputElement;
    const key = keyFor(input);
    if (key && !baseline.has(key)) {
      baseline.set(key, currentValue(input));
    }
  });
  updateButtonStates();
}

/**
 * Replace baselines with a snapshot of values that were actually submitted to
 * the cart. Use this instead of re-reading the DOM after a cart update —
 * otherwise edits the user made *during* the network round-trip would be
 * baselined out and silently dropped from the mobile auto-submit rerun loop.
 */
export function setBaselinesFromSnapshot(
  snapshot: Map<string, string>,
): void {
  baseline.clear();
  snapshot.forEach((value, key) => {
    baseline.set(key, value);
  });
  updateButtonStates();
}

/**
 * Return true if any input's current value differs from its recorded
 * baseline. Inputs without a baseline entry default to "" (useful for rows
 * that lazy-load after initial capture — they correctly start clean at zero
 * and become dirty on first keystroke).
 */
export function isAnyDirty(): boolean {
  let dirty = false;
  $(".variant_qty_input_directship").each(function (): false | void {
    const input = this as HTMLInputElement;
    const key = keyFor(input);
    if (!key) return;
    const current = currentValue(input);
    const base = baseline.get(key) ?? "";
    if (current !== base) {
      dirty = true;
      return false; // break
    }
  });
  return dirty;
}

/**
 * Keys (`variantId|warehouseCode`) whose input currently differs from its
 * baseline — i.e. rows the user has edited but not yet submitted.
 *
 * Cart sync uses this as a per-line apply guard: a remote change may land on
 * any row EXCEPT one the user is actively editing. One DOM pass, so callers
 * should hoist it out of loops.
 */
export function getDirtyKeys(): Set<string> {
  const dirty = new Set<string>();
  $(".variant_qty_input_directship").each(function () {
    const input = this as HTMLInputElement;
    const key = keyFor(input);
    if (!key) return;
    if (currentValue(input) !== (baseline.get(key) ?? "")) {
      dirty.add(key);
    }
  });
  return dirty;
}

/**
 * Merge baselines for specific keys, leaving every other entry untouched.
 *
 * This is the post-apply counterpart to setBaselinesFromSnapshot: after cart
 * sync applies a remote change to *some* rows, only those rows may be
 * re-baselined. captureBaseline() cannot do it (it never overwrites, so the
 * applied rows would stay permanently dirty and be excluded from every future
 * apply), and setBaselinesFromSnapshot() cannot either (it clears the map,
 * marking the user's held-back edits clean and losing them).
 */
export function setBaselineForKeys(values: Map<string, string>): void {
  values.forEach((value, key) => {
    baseline.set(key, value);
  });
  updateButtonStates();
}

/**
 * Enable/disable every `.inventory_submit` button based on global dirty state.
 * Adds `.is-clean` class for CSS styling hooks.
 */
export function updateButtonStates(): void {
  const dirty = isAnyDirty();
  $(".inventory_submit")
    .prop("disabled", !dirty)
    .toggleClass("is-clean", !dirty);
}
